import React from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';

import { scale } from '@components/frame';
import { ACCOUNT_AVATAR_SIZE } from '@components/webHeader/profile/constants';
import ColorWrapper, { WithColor } from '@styles/colors';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import TouchableElement from '@components/common/touchableElement';
import { SmallLoading } from '@components/loading';

import { useManageAccountChooser, getAccountInitial } from './hooks/useManageAccountChooser';

export interface AccountChooserProps extends WithColor {
  updateTokenState: (token: string) => void;
}

const AccountChooser = ({ C, updateTokenState }: AccountChooserProps) => {
  const {
    accounts,
    switchingAccount,
    removingMode,
    handleSelectAccount,
    handleUseAnotherAccount,
    handleRemoveAccount,
    toggleRemovingMode,
  } = useManageAccountChooser({ updateTokenState });

  const avatarStyle: ViewStyle = {
    width: scale(ACCOUNT_AVATAR_SIZE),
    height: scale(ACCOUNT_AVATAR_SIZE),
    borderRadius: scale(ACCOUNT_AVATAR_SIZE) / 2,
    backgroundColor: C('Background/Light/Field'),
    justifyContent: 'center',
    alignItems: 'center',
  };

  return (
    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: C('Background/Light/System') }}>
      <View
        style={{
          width: scale(380),
          borderRadius: scale(12),
          borderWidth: 1,
          borderColor: C('Hairline/Light/Primary'),
          backgroundColor: C('Background/Light/Modal'),
          paddingVertical: scale(24),
          paddingHorizontal: scale(20),
        }}
      >
        <Text style={[font['Headline'], { color: C('Label/Light/Primary'), textAlign: 'center', marginBottom: scale(20) }]}>
          {i18n.t('Choose an account')}
        </Text>

        {accounts.map((account) => {
          const isSwitching = switchingAccount === account.homeAccountId;

          return (
            <TouchableElement
              key={account.homeAccountId}
              onPress={
                removingMode
                  ? () => handleRemoveAccount(account.homeAccountId)
                  : () => handleSelectAccount(account.homeAccountId)
              }
              disabled={!removingMode && !!switchingAccount}
            >
              {({ boxStyle, pressHoverColor, hovered, pressed }) => (
                <View
                  style={[
                    {
                      flexDirection: 'row',
                      alignItems: 'center',
                      paddingVertical: scale(10),
                      paddingHorizontal: scale(12),
                      borderRadius: scale(10),
                    },
                    (hovered || pressed) && { backgroundColor: C(pressHoverColor('Background/Light/Field')) },
                    boxStyle,
                  ]}
                >
                  <View style={avatarStyle}>
                    <Text style={[font['Subhead 2'], { color: C('Label/Light/Secondary') }]}>{getAccountInitial(account)}</Text>
                  </View>
                  <View style={{ flex: 1, marginLeft: scale(12) }}>
                    <Text style={[font['Subhead 2'], { color: C('Label/Light/Primary') }]} numberOfLines={1}>
                      {account.name || account.username}
                    </Text>
                    <Text style={[font['Caption 1'], { color: C('Label/Light/Secondary'), marginTop: scale(2) }]} numberOfLines={1}>
                      {account.username}
                    </Text>
                  </View>
                  <View style={{ width: scale(24), alignItems: 'center' }}>
                    {isSwitching ? (
                      <SmallLoading />
                    ) : removingMode ? (
                      <Svg icon="close" color="Chart/Light/Warning" width={scale(14)} height={scale(14)} />
                    ) : null}
                  </View>
                </View>
              )}
            </TouchableElement>
          );
        })}

        <View style={{ marginTop: scale(8), borderTopWidth: 1, borderTopColor: C('Hairline/Light/Primary'), paddingTop: scale(8) }}>
          <TouchableElement onPress={handleUseAnotherAccount}>
            {({ boxStyle, pressHoverColor, hovered, pressed }) => (
              <View
                style={[
                  {
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingVertical: scale(10),
                    paddingHorizontal: scale(12),
                    borderRadius: scale(10),
                  },
                  (hovered || pressed) && { backgroundColor: C(pressHoverColor('Background/Light/Field')) },
                  boxStyle,
                ]}
              >
                <View style={avatarStyle}>
                  <Svg icon="plusSmall" width={scale(14)} height={scale(14)} color="Label/Light/Secondary" />
                </View>
                <Text style={[font['Subhead 2'], { color: C('Label/Light/Primary'), marginLeft: scale(12) }]}>
                  {i18n.t('Use another account')}
                </Text>
              </View>
            )}
          </TouchableElement>

          {accounts.length > 1 && <TouchableElement onPress={toggleRemovingMode}>
            {({ boxStyle, pressHoverColor, hovered, pressed }) => (
              <View
                style={[
                  {
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingVertical: scale(10),
                    paddingHorizontal: scale(12),
                    borderRadius: scale(10),
                  },
                  (hovered || pressed) && { backgroundColor: C(pressHoverColor('Background/Light/Field')) },
                  boxStyle,
                ]}
              >
                <View style={avatarStyle}>
                  <Svg icon="remove" width={scale(14)} height={scale(14)} color={removingMode ? 'Chart/Light/Warning' : 'Label/Light/Secondary'} />
                </View>
                <Text
                  style={[
                    font['Subhead 2'],
                    { color: removingMode ? C('Chart/Light/Warning') : C('Label/Light/Primary'), marginLeft: scale(12) },
                  ]}
                >
                  {removingMode ? i18n.t('Done') : i18n.t('Remove an account')}
                </Text>
              </View>
            )}
          </TouchableElement>}
        </View>
      </View>
    </View>
  );
};

export default ColorWrapper(AccountChooser);
